import {
  APP_NAME,
  type ContactView,
  type FollowerTimeline,
  type MessageDto,
  type PublicStep,
} from '@eventping/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  Bell,
  BellOff,
  Check,
  DoorOpen,
  EyeOff,
  Link2Off,
  Mail,
  Pencil,
  ShieldBan,
  Trash2,
  UserCheck,
  UserPlus,
} from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'
import { Card, FieldError, Footer, Page, SectionTitle, timeOf, Wordmark } from '@/components/shell'
import { Thread } from '@/components/thread'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ApiFailure, api, useLive, useMe, useTexts } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CreatorHeader } from './home'

/**
 * What a contact sees from the link in a text. No sign-in, no analytics, no third-party
 * requests: the token in the URL is the only credential, scoped to this person and event.
 */
export function LinkPage({ token }: { token: string }) {
  return <ContactPage base={`/api/l/${token}`} queryKey={['link', token]} mode="link" />
}

/**
 * The same view for a signed-in person who follows the event (/e/<id>): the session is the
 * credential, through their verified email or a contact added to their account.
 */
export function FollowedPage({ eventId }: { eventId: string }) {
  return <ContactPage base={`/api/f/${eventId}`} queryKey={['follow', eventId]} mode="session" />
}

function ContactPage({
  base,
  queryKey,
  mode,
}: {
  base: string
  queryKey: readonly string[]
  mode: 'link' | 'session'
}) {
  const me = useMe()
  // Texts-off mode: email controls only, and every number-only follower is asked for an email.
  // Once the view is in, its event's organizer decides (ContactView.textsEnabled: with
  // TEXTS_ALLOWLIST set, texts are on for some events only); until then, the site-wide answer.
  const siteTexts = useTexts()
  const view = useQuery({ queryKey, queryFn: () => api<ContactView>(base) })
  const texts = view.data?.textsEnabled ?? siteTexts
  useLive(view.data ? `${base}/stream` : null, queryKey)
  const [gone, setGone] = useState<null | 'left' | 'never'>(null)
  const header = me.data?.user && mode === 'session' ? <CreatorHeader me={me.data} /> : <Wordmark />

  if (gone) return <Goodbye kind={gone} header={header} texts={texts} />
  if (view.isPending) return <Page header={header}>{null}</Page>
  if (view.isError) {
    const status = view.error instanceof ApiFailure ? view.error.status : 0
    return (
      <Page header={header}>
        <Card className="text-center">
          <Link2Off className="mx-auto mb-3 size-10 text-muted-foreground" aria-hidden />
          <h1 className="text-xl font-semibold">
            {mode === 'session' && status === 401
              ? 'Sign in to see this event'
              : mode === 'session' && status === 404
                ? 'You’re not following this event'
                : status === 404
                  ? 'This link isn’t active anymore'
                  : 'Something went wrong'}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {mode === 'session' && status === 401 ? (
              <a href="/auth/login?returnTo=%2F" className="underline">
                Sign in
              </a>
            ) : mode === 'session' && status === 404 ? (
              'It may have ended your access, or you left it.'
            ) : status === 404 ? (
              texts ? (
                'Links expire after a while, and stop working if you leave an event. If you’re still following one, text LINK to the number your updates come from for a fresh link.'
              ) : (
                'Links expire after a while, and stop working if you leave an event. If you’re still following one, use the link in your newest update email, or get a fresh one below.'
              )
            ) : (
              'Please try again in a moment.'
            )}
          </p>
          {status === 404 && (
            <p className="mt-4">
              {mode === 'session' ? (
                <Link to="/" className="underline">
                  Back to your events
                </Link>
              ) : (
                <Link to="/recover" className="font-medium underline">
                  Lost your link? Get it by email
                </Link>
              )}
            </p>
          )}
        </Card>
        <Footer />
      </Page>
    )
  }

  const v = view.data
  const archived = v.event.status === 'archived'

  return (
    <Page header={header}>
      {mode === 'session' && (
        <Link
          to="/"
          className="mb-3 inline-flex min-h-10 items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> All events
        </Link>
      )}
      {v.account && <AccountNote base={base} state={v.account} queryKey={queryKey} />}
      <FollowerHeading v={v} greeting={v.me.firstName} />

      {texts && v.me.sms.offBy === 'creator' && <TextsOffNotice emailOn={v.me.email.on} />}
      {!v.me.email.masked && !archived && (!texts || v.me.sms.offBy !== 'creator') && (
        <AddEmailNudge texts={texts} />
      )}

      <FollowerTimelineCard steps={v.steps} />
      <FollowerConversation
        messages={v.messages}
        archived={archived}
        onSend={async (body) => {
          await api(`${base}/messages`, { body: { body } })
          // Shown at once, whether or not the live stream gets through.
          await view.refetch()
        }}
      />

      <Settings base={base} queryKey={queryKey} view={v} onGone={setGone} texts={texts} />
      <Footer
        texts={texts}
        note={
          texts ? `Reply STOP to any ${APP_NAME} text to stop all texts, HELP for help.` : undefined
        }
      />
    </Page>
  )
}

/**
 * The follower page's title block: greeting, event name, sender and the step count. Shared
 * with the organizer's preview (routes/preview.tsx).
 */
export function FollowerHeading({ v, greeting }: { v: FollowerTimeline; greeting: string }) {
  const done = v.steps.filter((s) => s.done).length
  return (
    <div className="mb-5">
      <p className="text-muted-foreground">Hi {greeting}</p>
      <h1 className="text-3xl font-semibold text-balance break-words">{v.event.title}</h1>
      <p className="mt-1 text-muted-foreground">
        Updates from {v.event.senderName}
        {/* Hidden steps are not in the list, so "3 of 3" would read as finished: once every
            step shown is done, just the count. */}
        {v.steps.length > 0 &&
          (done < v.steps.length
            ? ` · ${done} of ${v.steps.length} steps done`
            : ` · ${done} ${done === 1 ? 'step' : 'steps'} done`)}
        {v.event.status === 'archived' && ' · This event has ended'}
      </p>
    </div>
  )
}

/** The follower timeline. Shared with the organizer's preview. */
export function FollowerTimelineCard({ steps }: { steps: PublicStep[] }) {
  return (
    <Card className="mb-4">
      <SectionTitle>Timeline</SectionTitle>
      {steps.length === 0 && (
        <p className="text-muted-foreground">No steps yet. Check back soon.</p>
      )}
      <ol className="relative">
        {steps.map((s, i) => {
          const last = i === steps.length - 1
          const next = !s.done && steps.slice(0, i).every((p) => p.done)
          const hidden = s.hiddenUntilSent === true
          return (
            <li
              key={s.id}
              className="relative flex gap-4 pb-5 last:pb-0"
              data-testid={`timeline-${s.title}`}
              data-done={s.done}
              data-hidden-until-sent={hidden || undefined}
            >
              {!last && (
                <span
                  className={cn(
                    'absolute top-9 bottom-0 left-[15px] w-0.5',
                    s.done ? 'bg-done' : 'bg-border',
                  )}
                  aria-hidden
                />
              )}
              <span
                className={cn(
                  'relative z-10 grid size-8 shrink-0 place-items-center rounded-full',
                  s.done
                    ? 'bg-done text-white dark:text-background'
                    : hidden
                      ? 'border-2 border-dashed bg-card text-muted-foreground'
                      : next
                        ? 'border-2 border-primary bg-card'
                        : 'border-2 bg-card',
                )}
                aria-hidden
              >
                {s.done && <Check className="size-4.5" />}
                {hidden && <EyeOff className="size-4" />}
              </span>
              <div className="min-w-0 pt-0.5">
                <p
                  className={cn(
                    'font-medium break-words',
                    !s.done && 'text-muted-foreground',
                    next && !hidden && 'text-foreground',
                  )}
                >
                  {s.title}
                  <span className="sr-only">
                    {s.done ? ' (done)' : hidden ? ' (hidden until sent)' : ' (upcoming)'}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {s.done && s.doneAt
                    ? timeOf(s.doneAt)
                    : hidden
                      ? 'Hidden until sent: followers see it once it’s sent'
                      : next
                        ? 'Up next'
                        : 'Upcoming'}
                  {s.notify && (
                    <span className="ml-2 inline-flex items-center gap-1">
                      <Bell className="size-3.5" aria-hidden /> Notify step
                    </span>
                  )}
                </p>
                {s.note && <p className="mt-1">“{s.note}”</p>}
              </div>
            </li>
          )
        })}
      </ol>
    </Card>
  )
}

/**
 * The follower conversation card. Without `onSend` (the organizer's preview) the message box
 * is shown, as followers see it, but switched off.
 */
export function FollowerConversation({
  messages,
  archived,
  onSend,
}: {
  messages: MessageDto[]
  archived: boolean
  onSend?: (body: string) => Promise<void>
}) {
  return (
    <Card className="mb-4">
      <SectionTitle>Conversation</SectionTitle>
      <p className="-mt-2 mb-4 text-sm text-muted-foreground">Everyone following sees this.</p>
      <Thread
        messages={messages}
        viewer="contact"
        disabled={archived || !onSend}
        emptyText="No messages yet. Say hello!"
        onSend={onSend ?? (async () => {})}
      />
    </Card>
  )
}

/** A signed-in visitor on a personal link: is this event on their home page? */
function AccountNote({
  base,
  state,
  queryKey,
}: {
  base: string
  state: NonNullable<ContactView['account']>
  queryKey: readonly string[]
}) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (state !== 'offer') {
    return (
      <p className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <UserCheck className="size-4" aria-hidden /> This event is on your{' '}
        <Link to="/" className="underline">
          home page
        </Link>
        .
      </p>
    )
  }
  return (
    <Card className="mb-4 flex flex-wrap items-center gap-3">
      <p className="min-w-0 flex-1 text-sm">
        Keep this event on your home page, so you don’t need this link to find it.
      </p>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          try {
            await api(`${base}/account`, { method: 'POST' })
            toast.success('Added to your account')
            await client.invalidateQueries({ queryKey })
            await client.invalidateQueries({ queryKey: ['follows'] })
          } catch (e) {
            toast.error((e as Error).message)
          } finally {
            setBusy(false)
          }
        }}
      >
        <UserPlus /> Add this event to my account
      </Button>
    </Card>
  )
}

function Settings({
  base,
  queryKey,
  view,
  onGone,
  texts,
}: {
  base: string
  queryKey: readonly string[]
  view: ContactView
  onGone: (kind: 'left' | 'never') => void
  texts: boolean
}) {
  const [confirm, setConfirm] = useState<null | 'leave' | 'never'>(null)
  // "Never add me" works by number; in texts-off mode only a number-only follower has one.
  const canNever = texts || Boolean(view.me.sms.masked)

  return (
    <Card className="mb-4 scroll-mt-4" id="my-channels" data-testid="my-channels">
      <SectionTitle>How you get updates</SectionTitle>
      <MyChannels base={base} queryKey={queryKey} view={view} texts={texts} />
      <div className="mt-5 grid gap-2 border-t pt-4 sm:grid-cols-2">
        <Button variant="outline" onClick={() => setConfirm('leave')}>
          <DoorOpen /> Leave this event
        </Button>
        {canNever && (
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirm('never')}
          >
            <ShieldBan /> Never add me to events
          </Button>
        )}
      </div>

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirm === 'never' ? 'Never be added again?' : `Leave ${view.event.title}?`}
            </DialogTitle>
            <DialogDescription>
              {confirm === 'never'
                ? `You’ll leave every ${APP_NAME} event you’re in, and no one will be able to add your number again.${texts ? ' Texting START later undoes this.' : ''}`
                : 'You won’t get any more updates about this event, and this link will stop working.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const kind = confirm
                setConfirm(null)
                try {
                  await api(`${base}/${kind === 'never' ? 'never' : 'leave'}`, {
                    method: 'POST',
                  })
                  onGone(kind === 'never' ? 'never' : 'left')
                } catch (e) {
                  toast.error((e as Error).message)
                }
              }}
            >
              {confirm === 'never' ? 'Never add me' : 'Leave event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/**
 * Their own channels: an email to add, change, turn off or remove, and texts for this event
 * off (not a global STOP) or back on. Texts the organizer turned off stay off; adding an email
 * is how to keep getting updates.
 */
function MyChannels({
  base,
  queryKey,
  view,
  texts,
}: {
  base: string
  queryKey: readonly string[]
  view: ContactView
  /** Texts-off mode: no Texts switch, and email can't be removed from a number-only row. */
  texts: boolean
}) {
  const client = useQueryClient()
  const { email, sms } = view.me
  const [editing, setEditing] = useState(false)
  const [address, setAddress] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const showForm = editing || !email.masked

  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true)
    try {
      await fn()
      if (done) toast.success(done)
      await client.invalidateQueries({ queryKey })
      return true
    } catch (e) {
      toast.error((e as Error).message)
      return false
    } finally {
      setBusy(false)
    }
  }

  async function addEmail(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api(`${base}/email`, { body: { email: address } })
      toast.success('Email added. We sent you a confirmation.')
      setAddress('')
      setEditing(false)
      await client.invalidateQueries({ queryKey })
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const textsHint = sms.on
    ? 'Turning texts off here stops texts about this event only.'
    : sms.offBy === 'creator'
      ? 'Turned off by the organizer.'
      : sms.stopped
        ? 'Your number replied STOP. Text START to any EventPing message to turn texts back on.'
        : !sms.canTurnOn
          ? 'Text updates are full for this event.'
          : null

  return (
    <div className="space-y-3">
      {!(texts && sms.on) && !email.on && (!texts || sms.offBy !== 'creator') && (
        <p className="rounded-xl bg-muted px-4 py-3 text-sm" data-testid="link-only-note">
          You’re following by link only. Keep this page handy, or add an email to get updates.
        </p>
      )}

      <div className="rounded-2xl border p-3.5">
        {showForm ? (
          <form onSubmit={addEmail} className="space-y-2" noValidate>
            <Label htmlFor="my-email">{email.masked ? 'New email' : 'Add an email'}</Label>
            <div className="flex gap-2">
              <Input
                id="my-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                aria-invalid={Boolean(error) || undefined}
              />
              <Button type="submit" disabled={busy || !address.trim()} className="shrink-0">
                <Mail /> {email.masked ? 'Save' : 'Add'}
              </Button>
            </div>
            <FieldError>{error}</FieldError>
            {editing && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="my-email-switch" className="min-w-0">
                <span className="block font-medium">Email {email.on ? 'on' : 'off'}</span>
                <span className="block truncate text-sm text-muted-foreground">{email.masked}</span>
              </label>
              <Switch
                id="my-email-switch"
                checked={email.on}
                disabled={busy || (!email.on && email.offBy === 'creator')}
                onCheckedChange={(on) =>
                  void run(
                    () => api(`${base}/email/${on ? 'on' : 'off'}`, { method: 'POST' }),
                    on ? 'Email on.' : 'Email off for this event.',
                  )
                }
              />
            </div>
            {!email.on && email.offBy === 'creator' && (
              <p className="mt-1 text-sm text-muted-foreground">Turned off by the organizer.</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <Pencil /> Change
              </Button>
              {texts && sms.masked && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void run(() => api(`${base}/email`, { method: 'DELETE' }), 'Email removed.')
                  }
                >
                  <Trash2 /> Remove
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {texts && sms.masked && (
        <div className="rounded-2xl border p-3.5">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="my-texts-switch">
              <span className="block font-medium">Texts {sms.on ? 'on' : 'off'}</span>
              <span className="block text-sm text-muted-foreground">{sms.masked}</span>
            </label>
            <Switch
              id="my-texts-switch"
              data-testid="my-texts-switch"
              checked={sms.on}
              disabled={busy || (!sms.on && !sms.canTurnOn)}
              onCheckedChange={(on) =>
                void run(
                  () => api(`${base}/texts`, { body: { on } }),
                  on ? 'Texts on. Your next update comes by text.' : 'Texts off for this event.',
                )
              }
            />
          </div>
          {textsHint && <p className="mt-1 text-sm text-muted-foreground">{textsHint}</p>}
        </div>
      )}
    </div>
  )
}

/** At the top of the page for someone whose texts the organizer turned off. */
function TextsOffNotice({ emailOn }: { emailOn: boolean }) {
  return (
    <div
      role="status"
      data-testid="texts-off-notice"
      className="mb-4 flex gap-3 rounded-2xl border border-primary/40 bg-accent p-4 text-sm"
    >
      <BellOff className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>
        <strong className="font-semibold">Text updates were turned off by the organizer.</strong>{' '}
        {emailOn ? (
          'You still get email, and this page stays up to date.'
        ) : (
          <>
            This page stays up to date.{' '}
            <a href="#my-channels" className="font-medium underline">
              Add an email
            </a>{' '}
            to keep getting updates.
          </>
        )}
      </p>
    </div>
  )
}

/**
 * For someone who signed up before email was required (a number only): email is how every
 * update reaches them when texts pause, so ask for one. The form is in "How you get updates".
 */
function AddEmailNudge({ texts }: { texts: boolean }) {
  return (
    <div
      role="status"
      data-testid="add-email-nudge"
      className="mb-4 flex gap-3 rounded-2xl border border-primary/40 bg-accent p-4 text-sm"
    >
      <Mail className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>
        <strong className="font-semibold">Add your email to keep getting updates.</strong>{' '}
        {texts
          ? 'Email updates are free, so they keep coming even if texts pause.'
          : 'Updates come by email, and email is free.'}{' '}
        <a href="#my-channels" className="font-medium underline">
          Add an email
        </a>
      </p>
    </div>
  )
}

function Goodbye({
  kind,
  header,
  texts,
}: {
  kind: 'left' | 'never'
  header: React.ReactNode
  texts: boolean
}) {
  return (
    <Page header={header}>
      <Card className="text-center">
        <Check className="mx-auto mb-3 size-10 text-done" aria-hidden />
        <h1 className="text-xl font-semibold">
          {kind === 'never' ? 'You won’t be added to any events' : 'You’ve left this event'}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {kind === 'never'
            ? `We’ve removed you from every event, and no one can add your number again.${texts ? ' Text START if you ever change your mind.' : ''}`
            : `You won’t get any more ${texts ? 'texts' : 'updates'} about it.`}
        </p>
      </Card>
      <Footer texts={texts} />
    </Page>
  )
}
